<script setup>
import { watch, ref, computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
    ariaLabel: {
        type: String,
        default: undefined,
    },
    ariaLabelledBy: {
        type: String,
        default: undefined,
    },
    placeholder: {
        type: String,
        default: undefined,
    },
    limitChars: {
        type: Number,
        default: 1000,
    },
    text: {
        type: String,
        default: "",
    },
    error: {
        type: Boolean,
        default: false,
    },
    rows: {
        type: Number,
        default: 3,
    },
    maxlength: {
        type: Number,
        default: 100,
    },
    modelValue: {
        type: String,
        default: "",
    },
    longLabel: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["update:error", "update:modelValue"]);

const model = ref(props.modelValue);

watch(model, newValue => {
    emit("update:error", false);
    emit("update:modelValue", newValue);
});

// Watch for changes to modelValue prop and sync with internal model
watch(
    () => props.modelValue,
    newValue => {
        if (newValue !== model.value) {
            model.value = newValue;
        }
    }
);

const charCount = computed(() => model.value?.length || 0);
</script>

<template lang="pug">
.qmodii-textarea-container
  textarea(
      v-model="model",
      v-bind="$attrs",
      :aria-label="props.ariaLabel",
      :aria-labelledby="props.ariaLabelledBy",
      :placeholder="props.placeholder",
      :maxlength="props.limitChars",
      :class="['qmodii-textarea', { 'qmodii-error-border': props.error }]",
      :rows="rows"
  )
  .qmodii-char-count.qmodii-secondary-text(
      :class="{ 'qmodii-down-color': charCount === props.limitChars }"
  ) {{ charCount }} / {{ props.limitChars }}
      span(v-if="props.longLabel") {{ t("labels.characters") }}
</template>

<style lang="stylus">
.qmodii-textarea-container
    position relative
    width 100%

.qmodii-textarea
    width 100%
    min-height 120px
    padding 10px
    border 1px solid var(--qmodii-inputtext-border-color)
    border-radius var(--qmodii-border-radius-sm)
    line-height 1.5
    resize vertical
    transition border-color 0.2s
    box-sizing border-box

    &:focus
        border-color: var(--qmodii-inputtext-focus-border-color);
        box-shadow: var(--qmodii-inputtext-focus-ring-shadow);
        outline: var(--qmodii-inputtext-focus-ring-width) var(--qmodii-inputtext-focus-ring-style) var(--qmodii-inputtext-focus-ring-color);
        outline-offset: var(--qmodii-inputtext-focus-ring-offset);

    &::placeholder
        color var(--qmodii-placeholder-text)

.qmodii-char-count
    position absolute
    bottom -20px
    right 0px
    background var(--qmodii-background-color, white)  // Add background to prevent text bleeding through
    padding 2px 0px
    pointer-events none  // Prevent interference with textarea interaction

    span
        display inline-flex
        padding-left var(--qmodii-spacing-sm)

    &.qmodii-down-color
        color red   
</style>
